/**
 * Tour de France 2026 – Bingo Card
 *
 * Renders the final 3x3 bingo card as HTML (8 random items for the chosen
 * Fan/Hero side + a center logo cell). Item names are shown; descriptions are
 * kept in the markup for the (designer-supplied) click-to-reveal interaction.
 *
 * Download / copy / share snapshot the rendered card via html2canvas.
 */

/* global snapdom */
/* eslint-disable no-unused-vars */

var TDF2026 = (function () {
    'use strict';

    function TDF2026() {
        var data = (window.ys && window.ys.tdf2026) || {};
        this.cardEl   = document.getElementById('tdf26-bingo-card');
        this.items    = data.bingoItems || [];
        this.shareUrl = data.shareUrl || window.location.href;
        this.usertype = null;

        // Shared click-to-reveal description bubble (created lazily on first use).
        this.tipEl     = null;
        this.tipTextEl = null;
        this.tipTailEl = null;
        this._openCell = null;
    }

    function shuffle(arr) {
        return arr.slice().sort(function () { return 0.5 - Math.random(); });
    }

    /**
     * Build one bingo cell as DOM nodes (textContent escapes inherently).
     * A null cell is the center logo placeholder.
     */
    function makeCell(cell) {
        var div = document.createElement('div');
        div.className = 'tdf26-bingo-cell';

        if (cell === null) {
            div.className += ' tdf26-bingo-cell--logo';   // center logo cell
            return div;
        }

        var desc = cell.description == null ? '' : String(cell.description);
        div.setAttribute('data-description', desc);

        var nameEl = document.createElement('span');
        nameEl.className = 'tdf26-bingo-cell__name';
        nameEl.textContent = cell.name == null ? '' : String(cell.name);

        // Description is kept on data-description and shown via the shared bubble.
        div.appendChild(nameEl);
        return div;
    }

    /**
     * Resolve once every <img> inside the node has finished loading AND
     * decoding. The card images live in a hidden modal (deferred loading), so
     * without this the snapshot can fire before they're painted and capture a
     * half-rendered card.
     */
    function waitForImages(node) {
        var imgs = Array.prototype.slice.call(node.querySelectorAll('img'));
        return Promise.all(imgs.map(function (img) {
            var loaded;
            if (img.complete && img.naturalWidth > 0) {
                loaded = Promise.resolve();
            } else {
                loaded = new Promise(function (resolve) {
                    img.addEventListener('load', resolve, { once: true });
                    img.addEventListener('error', resolve, { once: true });
                });
            }
            return loaded.then(function () {
                // decode() guarantees the bitmap is ready to paint (load can
                // fire before that). Ignore failures — capture anyway.
                return img.decode ? img.decode().catch(function () {}) : null;
            });
        }));
    }

    // -------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------

    /**
     * Render the card for a given side ("Fan" / "Hero").
     */
    TDF2026.prototype.renderCard = function (usertype) {
        this.usertype = usertype;
        if (!this.cardEl) { return; }

        this.hideDescription();   // drop any open bubble before rebuilding the cells

        var pool = this.items.filter(function (it) {
            return !usertype || String(it.type).toLowerCase() === String(usertype).toLowerCase();
        });

        if (pool.length < 8) {
            console.warn('[TDF2026] Only ' + pool.length + ' bingo items available for "' + usertype + '" (need 8).');
        }

        var picked = shuffle(pool).slice(0, 8);
        // 3x3 grid: 4 items, center logo, 4 items.
        var cells = picked.slice(0, 4).concat([null]).concat(picked.slice(4, 8));

        // Replace ONLY the dynamic cells. Rewriting cardEl.innerHTML would re-create
        // the static <img crossorigin> overlays, forcing a re-fetch that hits a
        // CORS-poisoned cache entry and breaks the snapdom capture (download/copy/share).
        // Keep those nodes; swap just the cells.
        Array.prototype.forEach.call(
            this.cardEl.querySelectorAll('.tdf26-bingo-cell'),
            function (el) { el.parentNode.removeChild(el); }
        );

        var frag = document.createDocumentFragment();
        cells.forEach(function (cell) {
            frag.appendChild(makeCell(cell));
        });
        this.cardEl.appendChild(frag);
    };

    // -------------------------------------------------------------------------
    // Click-to-reveal description bubble
    // -------------------------------------------------------------------------

    /**
     * Lazily build the single shared description bubble as a child of the card.
     * It is NOT a `.tdf26-bingo-cell`, so renderCard's cell-removal loop leaves it
     * in place across re-renders.
     */
    TDF2026.prototype._ensureTip = function () {
        if (this.tipEl) { return this.tipEl; }
        if (!this.cardEl) { return null; }

        var tip = document.createElement('div');
        tip.className = 'tdf26-bingo-cell-tip';
        tip.setAttribute('aria-hidden', 'true');

        var text = document.createElement('span');
        text.className = 'tdf26-bingo-cell-tip__text';

        var tail = document.createElement('span');
        tail.className = 'tdf26-bingo-cell-tip__tail';

        tip.appendChild(text);
        tip.appendChild(tail);
        this.cardEl.appendChild(tip);

        this.tipEl     = tip;
        this.tipTextEl = text;
        this.tipTailEl = tail;
        return tip;
    };

    /**
     * Reveal the bubble against `cellEl`'s name text, nub pointing at the cell
     * centre. Anchored to the text (not the vertically-centred cell box) so it
     * hugs the words; the top row flips below the text since there's no room
     * above it. Positioned in the card's own coordinate space (offset*), so it
     * scales with the card's mobile zoom/transform and stays inside the card's
     * overflow:hidden box.
     */
    TDF2026.prototype.showDescription = function (cellEl) {
        if (!cellEl) { return; }

        var desc = cellEl.getAttribute('data-description');
        if (!desc) { return; }            // center logo / description-less cell

        var tip = this._ensureTip();
        if (!tip) { return; }

        // Top row (first 3 cells) has no room above → drop the bubble below the text.
        var cells = this.cardEl.querySelectorAll('.tdf26-bingo-cell');
        var below = Array.prototype.indexOf.call(cells, cellEl) < 3;

        this.tipTextEl.textContent = desc;
        tip.className = 'tdf26-bingo-cell-tip is-visible' + (below ? ' is-below' : '');

        var card  = this.cardEl;
        // Keep the bubble this far from the card's left/right edges (30 on desktop).
        var isDesktop = window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
        var pad   = isDesktop ? 30 : 16;
        var cardW = card.offsetWidth;
        var tipW  = tip.offsetWidth;
        var tipH  = tip.offsetHeight;

        // Anchor to the name text (cell text is centred, not top-aligned). NUB is how
        // far the circle protrudes past the bubble; GAP is its clearance from the text.
        var nameEl = cellEl.querySelector('.tdf26-bingo-cell__name') || cellEl;
        var cellCx = cellEl.offsetLeft + cellEl.offsetWidth / 2;
        var NUB = 10, GAP = 4;

        var left = cellCx - tipW / 2;
        left = Math.max(pad, Math.min(left, cardW - pad - tipW));   // clamp inside the card

        var top = below
            ? nameEl.offsetTop + nameEl.offsetHeight + NUB + GAP    // below the text
            : nameEl.offsetTop - NUB - GAP - tipH;                 // above the text
        top = Math.max(8, top);

        tip.style.left = left + 'px';
        tip.style.top  = top + 'px';

        var tailX = cellCx - left;                                  // keep the nub on the cell
        tailX = Math.max(16, Math.min(tailX, tipW - 16));
        this.tipTailEl.style.left = tailX + 'px';

        this._openCell = cellEl;
    };

    /** Hide the bubble (also called before every snapshot and re-render). */
    TDF2026.prototype.hideDescription = function () {
        if (this.tipEl) { this.tipEl.className = 'tdf26-bingo-cell-tip'; }
        this._openCell = null;
    };

    /** Click a cell: close it if it's the open one, otherwise reveal it. */
    TDF2026.prototype.toggleDescription = function (cellEl) {
        var isOpen = this.tipEl &&
            this.tipEl.className.indexOf('is-visible') !== -1 &&
            this._openCell === cellEl;

        if (isOpen) { this.hideDescription(); }
        else        { this.showDescription(cellEl); }
    };

    // -------------------------------------------------------------------------
    // Image capture (html2canvas)
    // -------------------------------------------------------------------------

    TDF2026.prototype._toBlob = function (cb) {
        // Never bake an open description bubble into the snapshot.
        this.hideDescription();

        var node = this.cardEl;
        if (!node || typeof snapdom === 'undefined') {
            console.warn('[TDF2026] snapdom unavailable.');
            cb(null);
            return;
        }

        // The card is absolutely positioned + vertically centered (top:50% /
        // translateY(-50%)) and may be zoomed on mobile. Neutralise those during
        // capture only, so the snapshot is the card's own box at full size.
        var s    = node.style;
        var prev = { top: s.top, transform: s.transform, zoom: s.zoom };
        s.top       = '0';
        s.transform = 'none';
        s.zoom      = '1';

        // Hide the cells' dotted "clickable" underline for the snapshot only
        // (CSS: .tdf26-bingo-card.is-capturing .tdf26-bingo-cell__name).
        node.classList.add('is-capturing');

        function restore() {
            s.top       = prev.top;
            s.transform = prev.transform;
            s.zoom      = prev.zoom;
            node.classList.remove('is-capturing');
        }

        // Wait for images AND web fonts before snapshotting. embedFonts:true makes
        // snapdom inline the (same-origin) "SKODA Next" font as a subsetted base64
        // data-URI inside the SVG — Safari/WebKit won't use the page's loaded fonts
        // inside the detached <foreignObject> (it bakes Arial otherwise), and
        // snapdom's built-in Safari multi-pass render only runs when embedFonts is on.
        var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
        Promise.all([waitForImages(node), fontsReady])
            .then(function () { return snapdom(node, { scale: 2, backgroundColor: null, embedFonts: true }); })
            .then(function (result) { return result.toBlob({ type: 'png' }); })
            .then(function (blob) { restore(); cb(blob); })
            .catch(function (err) {
                restore();
                console.warn('[TDF2026] snapdom failed:', err);
                cb(null);
            });
    };

    

    TDF2026.prototype.downloadCard = function () {
        this._toBlob(function (blob) {
            if (!blob) { return; }
            var url = URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = 'tdf-2026-bingo-card.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    };

    TDF2026.prototype.copyCard = function () {
        var self = this;

        // Image clipboard needs a secure context (HTTPS / localhost) + ClipboardItem.
        if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
            this._toBlob(function (blob) {
                if (!blob) { self._copyLink(); return; }
                navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
                    .catch(function (e) {
                        console.warn('[TDF2026] image copy failed, copying link instead:', e);
                        self._copyLink();
                    });
            });
            return;
        }

        // No image clipboard (e.g. non-HTTPS dev) → copy the page link instead.
        console.warn('[TDF2026] Image clipboard unavailable (needs HTTPS); copying link.');
        this._copyLink();
    };

    /**
     * Public: copy the share URL to the clipboard (desktop "Copy" action).
     */
    TDF2026.prototype.copyLink = function () {
        this._copyLink();
    };

    /**
     * Copy the share URL to the clipboard, with a legacy fallback that also
     * works in insecure contexts.
     */
    TDF2026.prototype._copyLink = function () {
        var text = this.shareUrl;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(function () {});
            return;
        }

        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { /* noop */ }
        document.body.removeChild(ta);
    };

    /**
     * Native share with the card image (mobile); falls back to download.
     */
    TDF2026.prototype.shareCard = function () {
        var self = this;
        this._toBlob(function (blob) {
            if (blob && navigator.share) {
                var file = new File([blob], 'tdf-2026-bingo-card.png', { type: 'image/png' });
                if (!navigator.canShare || navigator.canShare({ files: [file] })) {
                    // Share the image only — no title/text. Some apps (e.g.
                    // Slack) post the text and drop the file when both are sent.
                    navigator.share({ files: [file] })
                        .catch(function (err) {
                            if (err && err.name !== 'AbortError') { self.downloadCard(); }
                        });
                    return;
                }
            }
            self.downloadCard();
        });
    };

    /**
     * Open a social share intent for the page URL.
     */
    TDF2026.prototype.shareTo = function (network) {
        var url  = encodeURIComponent(this.shareUrl);
        var text = encodeURIComponent('My Epiq Bingo Card – Tour de France 2026');
        var dest = '';

        switch (network) {
            case 'facebook': dest = 'https://www.facebook.com/sharer/sharer.php?u=' + url; break;
            case 'x':        dest = 'https://twitter.com/intent/tweet?url=' + url + '&text=' + text; break;
            case 'whatsapp': dest = 'https://wa.me/?text=' + text + '%20' + url; break;
            case 'mail':     dest = 'mailto:?subject=' + text + '&body=' + text + '%20' + url; break;
        }

        if (dest) { window.open(dest, '_blank', 'noopener'); }
    };

    return TDF2026;
})();
